import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface DashboardMetrics {
  totalContacts: number;
  totalContactsLastMonth: number;
  activeLeaders: number;
  activeLeadersLastMonth: number;
  newThisMonth: number;
}

export interface RecentActivity {
  id: string;
  type: 'contact';
  text: string;
  time: string;
  createdAt: Date;
}

async function fetchMetrics(): Promise<DashboardMetrics> {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  // startOfLastMonth (reserved for future use)
  const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59).toISOString();

  const [
    { count: totalContacts },
    { count: totalContactsLastMonth },
    { count: activeLeaders },
    { count: activeLeadersLastMonth },
    { count: newThisMonth },
  ] = await Promise.all([
    // Total de contatos ativos
    supabase
      .from('contacts')
      .select('*', { count: 'exact', head: true })
      .eq('active', true),

    // Total de contatos ativos no fim do mês passado
    supabase
      .from('contacts')
      .select('*', { count: 'exact', head: true })
      .eq('active', true)
      .lte('created_at', endOfLastMonth),

    // Lideranças ativas
    supabase
      .from('contacts')
      .select('*', { count: 'exact', head: true })
      .eq('active', true)
      .eq('category', 'lideranca'),

    // Lideranças ativas até fim do mês passado
    supabase
      .from('contacts')
      .select('*', { count: 'exact', head: true })
      .eq('active', true)
      .eq('category', 'lideranca')
      .lte('created_at', endOfLastMonth),

    // Novos contatos este mês
    supabase
      .from('contacts')
      .select('*', { count: 'exact', head: true })
      .eq('active', true)
      .gte('created_at', startOfMonth),
  ]);

  return {
    totalContacts: totalContacts ?? 0,
    totalContactsLastMonth: totalContactsLastMonth ?? 0,
    activeLeaders: activeLeaders ?? 0,
    activeLeadersLastMonth: activeLeadersLastMonth ?? 0,
    newThisMonth: newThisMonth ?? 0,
  };
}

async function fetchRecentActivity(): Promise<RecentActivity[]> {
  const { data, error } = await supabase
    .from('contacts')
    .select('id, full_name, category, created_at, updated_at, neighborhood')
    .eq('active', true)
    .order('created_at', { ascending: false })
    .limit(5);

  if (error) throw error;

  return (data ?? []).map((c) => {
    const createdAt = new Date(c.created_at as string);
    const diffMs = Date.now() - createdAt.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffH = Math.floor(diffMin / 60);
    const diffD = Math.floor(diffH / 24);

    let timeLabel: string;
    if (diffMin < 60) timeLabel = `${diffMin} min atrás`;
    else if (diffH < 24) timeLabel = `${diffH} h atrás`;
    else timeLabel = `${diffD} d atrás`;

    const neighborhood = (c.neighborhood as string | null) ? ` no bairro ${c.neighborhood}` : '';
    const name = (c.full_name as string) || 'Contato';
    const cat = (c.category as string) || 'eleitor';
    const catLabel =
      cat === 'lideranca' ? 'Liderança' :
      cat === 'voluntario' ? 'Voluntário' :
      cat === 'doador'     ? 'Doador'    :
      cat === 'simpatizante' ? 'Simpatizante' :
      'Eleitor';

    return {
      id: c.id as string,
      type: 'contact' as const,
      text: `${catLabel} ${name} cadastrado${neighborhood}`,
      time: timeLabel,
      createdAt,
    };
  });
}

// ── WhatsApp delivery rate ────────────────────────────────────────────────────

export interface WhatsAppDeliveryRate {
  /** Taxa de entrega (0–100). */
  rate:           number;
  /** Taxa do período anterior para variação. */
  ratePrevPeriod: number;
  /** Total de mensagens enviadas no período atual. */
  totalSent:      number;
  /** false quando não há nenhuma campanha WhatsApp ainda. */
  hasData:        boolean;
}

async function fetchDeliveryRate(): Promise<WhatsAppDeliveryRate> {
  const now           = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
  const sixtyDaysAgo  = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000).toISOString();

  const [currRes, prevRes] = await Promise.all([
    supabase
      .from('campaigns')
      .select('stats_sent, stats_delivered')
      .eq('type', 'whatsapp')
      .in('status', ['completed', 'running', 'paused'])
      .gte('started_at', thirtyDaysAgo),

    supabase
      .from('campaigns')
      .select('stats_sent, stats_delivered')
      .eq('type', 'whatsapp')
      .in('status', ['completed', 'running', 'paused'])
      .gte('started_at', sixtyDaysAgo)
      .lt('started_at', thirtyDaysAgo),
  ]);

  type Row = { stats_sent: number; stats_delivered: number };
  const currRows = (currRes.data ?? []) as Row[];
  const prevRows = (prevRes.data ?? []) as Row[];

  const currSent      = currRows.reduce((acc, r) => acc + (r.stats_sent      ?? 0), 0);
  const currDelivered = currRows.reduce((acc, r) => acc + (r.stats_delivered ?? 0), 0);
  const prevSent      = prevRows.reduce((acc, r) => acc + (r.stats_sent      ?? 0), 0);
  const prevDelivered = prevRows.reduce((acc, r) => acc + (r.stats_delivered ?? 0), 0);

  const rate     = currSent > 0 ? Math.round((currDelivered / currSent) * 100) : 0;
  const prevRate = prevSent > 0 ? Math.round((prevDelivered / prevSent) * 100) : 0;

  return { rate, ratePrevPeriod: prevRate, totalSent: currSent, hasData: currSent > 0 };
}

export function useWhatsAppDeliveryRate() {
  return useQuery({
    queryKey: ['dashboard', 'whatsapp-delivery-rate'],
    queryFn: fetchDeliveryRate,
    staleTime: 5 * 60_000,
    refetchInterval: 10 * 60_000,
  });
}

/** Formata variação percentual vs período anterior */
export function formatChange(current: number, previous: number): { label: string; positive: boolean } {
  if (previous === 0) {
    return current > 0
      ? { label: `+${current} novos`, positive: true }
      : { label: '—', positive: true };
  }
  const pct = ((current - previous) / previous) * 100;
  const sign = pct >= 0 ? '+' : '';
  return {
    label: `${sign}${pct.toFixed(1)}%`,
    positive: pct >= 0,
  };
}

export function useDashboardMetrics() {
  return useQuery({
    queryKey: ['dashboard', 'metrics'],
    queryFn: fetchMetrics,
    staleTime: 2 * 60_000,   // 2 min
    refetchInterval: 5 * 60_000, // revalida a cada 5 min
  });
}

export function useDashboardActivity() {
  return useQuery({
    queryKey: ['dashboard', 'activity'],
    queryFn: fetchRecentActivity,
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
  });
}
